/**
 * 本地存储服务
 * 使用 IndexedDB 存储所有文档数据
 * 所有完整内容数据保存在用户浏览器本地
 * 仅将操作元数据同步到后端供管理员查看
 */

import axios from 'axios';

// 创建 axios 实例用于同步元数据
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 
    (window.location.hostname === 'localhost' ? 'http://localhost:8000' : window.location.origin),
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加 token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const DB_NAME = 'OfficialDocumentDB';
const DB_VERSION = 1;

// 存储对象名称
const STORES = {
  DOCUMENTS: 'documents',           // 公文排版历史
  SCHEDULES: 'schedules',           // 行程排版历史
  TABLE_SIGNS: 'tableSigns',        // 台签排版历史
  UNIT_SORTS: 'unitSorts',          // 发文单位排版历史
  UNIT_GROUPS: 'unitGroups',        // 单位组别数据
  DRAFTS: 'drafts'                  // 草稿
};

// store 名称到 module 的映射（用于后端同步）
const STORE_TO_MODULE = {
  [STORES.DOCUMENTS]: 'document',
  [STORES.SCHEDULES]: 'schedule',
  [STORES.TABLE_SIGNS]: 'tableSign',
  [STORES.UNIT_SORTS]: 'unitSort'
};

class LocalStorageService {
  constructor() {
    this.db = null;
    this.initPromise = this.initDB();
  }

  /**
   * 初始化 IndexedDB
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB 打开失败:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB 初始化成功');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // 创建各种存储对象
        if (!db.objectStoreNames.contains(STORES.DOCUMENTS)) {
          db.createObjectStore(STORES.DOCUMENTS, { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains(STORES.SCHEDULES)) {
          db.createObjectStore(STORES.SCHEDULES, { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains(STORES.TABLE_SIGNS)) {
          db.createObjectStore(STORES.TABLE_SIGNS, { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains(STORES.UNIT_SORTS)) {
          db.createObjectStore(STORES.UNIT_SORTS, { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains(STORES.UNIT_GROUPS)) {
          db.createObjectStore(STORES.UNIT_GROUPS, { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains(STORES.DRAFTS)) {
          db.createObjectStore(STORES.DRAFTS, { keyPath: 'id', autoIncrement: true });
        }
      };
    });
  }

  /**
   * 确保数据库已初始化
   */
  async ensureDB() {
    if (!this.db) {
      await this.initPromise;
    }
    return this.db;
  }

  /**
   * 同步历史记录元数据到后端（仅同步操作信息，不同步具体内容）
   * @param {string} storeName - 存储名称
   * @param {object} data - 数据对象
   * @param {string} action - 操作类型: create, update, delete
   */
  async syncMetadataToBackend(storeName, data, action = 'create') {
    const module = STORE_TO_MODULE[storeName];
    if (!module) return; // 只有指定的模块才需要同步

    try {
      // 获取同步ID（使用本地数据的ID，加上storeName前缀避免不同store之间的id冲突）
      const syncId = data.id ? `${storeName}_${data.id}` : `${module}_${Date.now()}`;
      
      // 构建标题
      let title = data.title || '';
      if (!title && data.documentData && data.documentData.title) {
        title = data.documentData.title;
      }
      if (!title && data.scheduleData && data.scheduleData.title) {
        title = data.scheduleData.title;
      }
      if (!title) {
        title = `${this.getModuleDisplayName(module)}_${new Date().toLocaleString('zh-CN')}`;
      }

      // 调用后端API同步元数据
      await api.post('/api/history/sync-metadata', {
        module: module,
        title: title,
        sync_id: syncId,
        action: action
      });
      
      console.log(`✅ 元数据同步成功: ${module} - ${title}`);
    } catch (error) {
      // 同步失败不影响本地操作，仅记录日志
      console.warn('⚠️ 元数据同步失败（不影响本地保存）:', error.message);
    }
  }

  /**
   * 删除后端元数据
   * @param {string} storeName - 存储名称
   * @param {string|number} id - 数据ID
   */
  async deleteMetadataFromBackend(storeName, id) {
    const module = STORE_TO_MODULE[storeName];
    if (!module) return;

    try {
      const syncId = String(id);
      await api.delete(`/api/history/sync-metadata/${syncId}`);
      console.log(`✅ 元数据删除成功: ${module} - ${syncId}`);
    } catch (error) {
      console.warn('⚠️ 元数据删除失败（不影响本地删除）:', error.message);
    }
  }

  /**
   * 获取模块显示名称
   */
  getModuleDisplayName(module) {
    const names = {
      'document': '公文排版',
      'schedule': '行程排版',
      'tableSign': '台签排版',
      'unitSort': '发文单位排版'
    };
    return names[module] || module;
  }

  /**
   * 通用保存方法
   */
  async save(storeName, data) {
    await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      
      // 添加时间戳
      const dataWithTimestamp = {
        ...data,
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const request = store.put(dataWithTimestamp);

      request.onsuccess = async () => {
        const result = { ...dataWithTimestamp, id: request.result };
        
        // 同步元数据到后端（仅操作信息，不包含具体内容）
        const action = data.id ? 'update' : 'create';
        await this.syncMetadataToBackend(storeName, result, action);
        
        resolve(result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * 通用获取所有数据方法
   */
  async getAll(storeName) {
    await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        // 按更新时间倒序排列
        const results = request.result.sort((a, b) => 
          new Date(b.updatedAt) - new Date(a.updatedAt)
        );
        resolve(results);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * 通用获取单个数据方法
   */
  async getById(storeName, id) {
    await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(id);

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * 通用删除方法
   */
  async delete(storeName, id) {
    await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(id);

      request.onsuccess = async () => {
        // 同步删除后端元数据
        await this.deleteMetadataFromBackend(storeName, id);
        
        resolve(true);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * 清空某个存储对象的所有数据
   */
  async clear(storeName) {
    await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  // ==================== 公文排版相关 ====================

  async saveDocument(documentData) {
    return await this.save(STORES.DOCUMENTS, documentData);
  }

  async getAllDocuments() {
    return await this.getAll(STORES.DOCUMENTS);
  }

  async getDocument(id) {
    return await this.getById(STORES.DOCUMENTS, id);
  }

  async deleteDocument(id) {
    return await this.delete(STORES.DOCUMENTS, id);
  }

  // ==================== 行程排版相关 ====================

  async saveSchedule(scheduleData) {
    return await this.save(STORES.SCHEDULES, scheduleData);
  }

  async getAllSchedules() {
    return await this.getAll(STORES.SCHEDULES);
  }

  async getSchedule(id) {
    return await this.getById(STORES.SCHEDULES, id);
  }

  async deleteSchedule(id) {
    return await this.delete(STORES.SCHEDULES, id);
  }

  // ==================== 台签排版相关 ====================

  async saveTableSign(tableSignData) {
    return await this.save(STORES.TABLE_SIGNS, tableSignData);
  }

  async getAllTableSigns() {
    return await this.getAll(STORES.TABLE_SIGNS);
  }

  async getTableSign(id) {
    return await this.getById(STORES.TABLE_SIGNS, id);
  }

  async deleteTableSign(id) {
    return await this.delete(STORES.TABLE_SIGNS, id);
  }

  // ==================== 发文单位排版相关 ====================

  async saveUnitSort(unitSortData) {
    return await this.save(STORES.UNIT_SORTS, unitSortData);
  }

  async getAllUnitSorts() {
    return await this.getAll(STORES.UNIT_SORTS);
  }

  async getUnitSort(id) {
    return await this.getById(STORES.UNIT_SORTS, id);
  }

  async deleteUnitSort(id) {
    return await this.delete(STORES.UNIT_SORTS, id);
  }

  // ==================== 单位组别相关 ====================

  async saveUnitGroups(groupsData) {
    // 单位组别只有一个记录，使用固定ID
    const existing = await this.getAll(STORES.UNIT_GROUPS);
    if (existing.length > 0) {
      return await this.save(STORES.UNIT_GROUPS, { ...groupsData, id: existing[0].id });
    } else {
      return await this.save(STORES.UNIT_GROUPS, groupsData);
    }
  }

  async getUnitGroups() {
    const result = await this.getAll(STORES.UNIT_GROUPS);
    return result.length > 0 ? result[0] : null;
  }

  // ==================== 草稿相关 ====================

  async saveDraft(draftData) {
    return await this.save(STORES.DRAFTS, draftData);
  }

  async getAllDrafts() {
    return await this.getAll(STORES.DRAFTS);
  }

  async getDraft(id) {
    return await this.getById(STORES.DRAFTS, id);
  }

  async deleteDraft(id) {
    return await this.delete(STORES.DRAFTS, id);
  }

  // ==================== 数据导出/导入 ====================

  /**
   * 导出所有数据（用于备份）
   */
  async exportAllData() {
    await this.ensureDB();
    
    const data = {
      documents: await this.getAllDocuments(),
      schedules: await this.getAllSchedules(),
      tableSigns: await this.getAllTableSigns(),
      unitSorts: await this.getAllUnitSorts(),
      unitGroups: await this.getUnitGroups(),
      drafts: await this.getAllDrafts(),
      exportTime: new Date().toISOString()
    };

    return data;
  }

  /**
   * 导入数据（用于恢复备份）
   */
  async importData(data) {
    await this.ensureDB();

    try {
      if (data.documents) {
        for (const item of data.documents) {
          await this.save(STORES.DOCUMENTS, item);
        }
      }
      if (data.schedules) {
        for (const item of data.schedules) {
          await this.save(STORES.SCHEDULES, item);
        }
      }
      if (data.tableSigns) {
        for (const item of data.tableSigns) {
          await this.save(STORES.TABLE_SIGNS, item);
        }
      }
      if (data.unitSorts) {
        for (const item of data.unitSorts) {
          await this.save(STORES.UNIT_SORTS, item);
        }
      }
      if (data.unitGroups) {
        await this.save(STORES.UNIT_GROUPS, data.unitGroups);
      }
      if (data.drafts) {
        for (const item of data.drafts) {
          await this.save(STORES.DRAFTS, item);
        }
      }

      return true;
    } catch (error) {
      console.error('导入数据失败:', error);
      throw error;
    }
  }
}

// 导出单例
const localStorageService = new LocalStorageService();
export default localStorageService;
